import { GetAccountsResponse } from '@features/accounts/api/accounts/get-accounts';
import { TransferState, TransferSetState, TransferType } from '@features/transfers/enums';
import { TransferSet } from '@features/transfers/types';
import { api } from '@src/lib/api-client';
import { queryClient, QueryConfig } from '@src/lib/react-query';
import { useRefreshAccountJobsStore } from '@src/stores';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { ToastUtility } from '@utils';
import { AxiosResponse } from 'axios';
import { useEffect, useRef } from 'react';

export type GetTransferSetsData = {
  optimizationInProgress: boolean;
  optimizationPending: boolean;
  transferSets: TransferSet[];
};

export async function getTransferSets(): Promise<GetTransferSetsData> {
  const res: AxiosResponse<TransferSet[]> = await api.get(
    `/api/v3/user/transfer-sets`,
  );
  return {
    optimizationInProgress: res?.data.some(
      (set) => set.type === TransferType.OPTIMIZATION,
    ),
    optimizationPending: res?.data.some(
      (set) =>
        set.type === TransferType.OPTIMIZATION &&
        set.state === TransferSetState.PENDING,
    ),
    transferSets: res?.data,
  };
}

export const getTransferSetsQueryOptions = queryOptions({
  queryKey: ['transferSets'],
  queryFn: getTransferSets,
  throwOnError: false,
  initialData: {
    optimizationInProgress: false,
    optimizationPending: false,
    transferSets: [],
  },
  initialDataUpdatedAt: 0,
  staleTime: 30 * 1000, // 30 seconds — prevents rapid re-fetches from calling API multiple times
});

type UseGetTransferSetsOptions = {
  queryConfig?: QueryConfig<typeof getTransferSetsQueryOptions>;
};

export const useGetTransferSets = ({
  queryConfig,
}: UseGetTransferSetsOptions = {}) => {
  return useQuery({ ...getTransferSetsQueryOptions, ...queryConfig });
};

const TERMINAL_TRANSFER_STATES = new Set([
  TransferState.FAILED,
  TransferState.COMPLETED,
  TransferState.CANCELED,
]);

// Schedules a one-time series of refetches at 1, 2, 5, and 10 minutes after
// the initial fetch if any transfer set was created within the last 10 minutes.
// Must be called in a single parent component to avoid duplicate polling.
// After each refetch, triggers balance refreshes for accounts whose transfers
// completed, failed, or were cancelled, or when the transfer sets array empties.
export const useGetTransferSetsPolling = () => {
  const query = useQuery({ ...getTransferSetsQueryOptions });
  // Ref persists the timeout IDs across re-renders so the schedule is only
  // created once, even when query data updates after each refetch.
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  // Always reflects the latest query data so timeout callbacks can read it.
  const latestDataRef = useRef(query.data);
  // Tracks transfer UUIDs already processed to avoid duplicate refresh jobs.
  const processedTransferUuidsRef = useRef<Set<string>>(new Set());
  const { addRefreshAccountJob } = useRefreshAccountJobsStore.use.actions();

  useEffect(() => {
    latestDataRef.current = query.data;
  }, [query.data]);

  const triggerRefreshForAccountUuids = (uuids: string[]) => {
    const accounts = queryClient.getQueryData<GetAccountsResponse>([
      'accounts',
      'list',
    ]);

    for (const uuid of uuids) {
      const internal = accounts?.all.find((a) => a.uuid === uuid);
      if (internal) {
        addRefreshAccountJob({ accountId: uuid, bankName: internal.name });
        continue;
      }
    }
  };

  const triggerRefreshForTransferSets = (transferSets: TransferSet[]) => {
    const uuids = new Set<string>();
    for (const set of transferSets) {
      for (const transfer of set.transfers) {
        uuids.add(transfer.sourceAccountUuid);
        uuids.add(transfer.destinationAccountUuid);
      }
    }
    triggerRefreshForAccountUuids([...uuids]);
  };

  useEffect(() => {
    // Guard: don't reschedule if polling has already started
    if (timeoutsRef.current.length > 0) {
      return;
    }

    const hasRecentTransferSet = query.data?.transferSets.some((set) => {
      const createdAt = new Date(set.createdAt);
      const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
      return createdAt >= tenMinutesAgo;
    });

    if (!hasRecentTransferSet) {
      return;
    }

    timeoutsRef.current = [1, 2, 5, 10].map((minutes) =>
      setTimeout(
        async () => {
          const previousTransferSets =
            latestDataRef.current?.transferSets ?? [];
          const result = await query.refetch();
          const newTransferSets = result.data?.transferSets ?? [];

          // Transfer sets went from non-empty to empty: refresh all accounts
          // that were part of the now-gone transfers using the previous data,
          // then cancel any remaining scheduled fetches.
          if (previousTransferSets.length > 0 && newTransferSets.length === 0) {
            triggerRefreshForTransferSets(previousTransferSets);
            timeoutsRef.current.forEach((t) => clearTimeout(t));
            timeoutsRef.current = [];
            return;
          }

          // Refresh accounts for any transfer that newly reached a terminal state.
          for (const set of newTransferSets) {
            for (const transfer of set.transfers) {
              if (
                !TERMINAL_TRANSFER_STATES.has(transfer.state) ||
                processedTransferUuidsRef.current.has(transfer.uuid)
              ) {
                continue;
              }
              processedTransferUuidsRef.current.add(transfer.uuid);
              triggerRefreshForAccountUuids([
                transfer.sourceAccountUuid,
                transfer.destinationAccountUuid,
              ]);
            }
          }
        },
        minutes * 60 * 1000,
      ),
    );
  }, [query.data?.transferSets, query.refetch]);

  // Separate effect with empty deps so cleanup only runs on unmount,
  // not after each refetch (which would cancel the remaining timeouts).
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    };
  }, []);
};

export const callGetTransferSetsErrorToast = () => {
  ToastUtility.error('Failed to fetch Transfer Sets', {
    toastId: 'transferSet',
  });
};
